import { useContentShareState, useMeetingManager, useRosterState } from 'amazon-chime-sdk-component-library-react';
import { useEffect, useState, useRef } from 'react';
import { useVideoConfig } from '../components/video/video-config-provider';
const MOVING_AVERAGE_LENGTH = 10;
const DEGRADE_FREQUENCY = 1000;
const MIN_FOCUS_TIME = 3000;
const UPDATE_FOCUS_INTERVAL = 500;
export const useAttendees = () => {
    var _a, _b, _c;
    const currentAttendeeId = (_b = (_a = useMeetingManager().configuration) === null || _a === void 0 ? void 0 : _a.credentials) === null || _b === void 0 ? void 0 : _b.attendeeId;
    const sharingAttendeeId = (_c = useContentShareState().sharingAttendeeId) === null || _c === void 0 ? void 0 : _c.replace('#content', '');
    const { knownClients, webinar } = useVideoConfig();
    const { roster } = useRosterState();
    const attendees = [];
    const hostAttendeeIds = knownClients.filter(({ isHost }) => isHost).map(({ clientId }) => clientId);
    if (webinar) {
        attendees.push(sharingAttendeeId, ...hostAttendeeIds);
    }
    else {
        attendees.push(currentAttendeeId, sharingAttendeeId, ...Object.keys(roster));
    }
    const visibleAttendees = attendees
        .filter(attendee => knownClients.find(({ clientId, isHidden }) => clientId === attendee && !isHidden))
        .filter((attendeeId, index, self) => attendeeId && self.indexOf(attendeeId) === index);
    const focusedAttendeeId = useFocusedAttendee(visibleAttendees, [currentAttendeeId]);
    return {
        focusedAttendeeId: sharingAttendeeId || focusedAttendeeId,
        currentAttendeeId,
        sharingAttendeeId,
        visibleAttendees,
    };
};
const useFocusedAttendee = (allAttendees, ignoredAttendees = []) => {
    const attendees = allAttendees.filter(attendee => !ignoredAttendees.includes(attendee));
    const meetingManager = useMeetingManager();
    const scoresRef = useRef({});
    const [focusedAttendee, setFocusedAttendee] = useState(attendees[0]);
    const focusedTimeRef = useRef(0);
    useEffect(() => {
        const updateScoresInterval = setInterval(() => {
            const now = new Date().getTime();
            if (now - focusedTimeRef.current >= MIN_FOCUS_TIME) {
                let recordScore = 0;
                let recordAttendee = focusedAttendee;
                Object.entries(scoresRef.current).forEach(([attendee, score]) => {
                    if (score > recordScore) {
                        recordScore = score;
                        recordAttendee = attendee;
                    }
                });
                if (recordAttendee !== focusedAttendee) {
                    setFocusedAttendee(recordAttendee);
                    focusedTimeRef.current = now;
                }
            }
        }, UPDATE_FOCUS_INTERVAL);
        return () => {
            clearInterval(updateScoresInterval);
        };
    }, []);
    useEffect(() => {
        const degraderInterval = setInterval(() => {
            scoresRef.current = degradeScores(scoresRef.current);
        }, DEGRADE_FREQUENCY);
        return () => {
            clearInterval(degraderInterval);
        };
    }, []);
    useEffect(() => {
        scoresRef.current = clearInvisibleScores(scoresRef.current, attendees);
        if (!focusedAttendee) {
            setFocusedAttendee(attendees[0]);
            focusedTimeRef.current = new Date().getTime();
        }
        const destructors = [];
        attendees.forEach(attendee => {
            const handler = (attendeeId, volume, muted) => {
                if (muted) {
                    scoresRef.current = updateScore(scoresRef.current, attendeeId, 0, true);
                    return;
                }
                if (volume === null && !Object.is(scoresRef.current[attendeeId], undefined)) {
                    scoresRef.current = updateScore(scoresRef.current, attendeeId, scoresRef.current[attendeeId]);
                    return;
                }
                scoresRef.current = updateScore(scoresRef.current, attendeeId, volume || 0, muted);
            };
            // For some reason audioVideo might not be ready here yet sometimes
            if (meetingManager.audioVideo) {
                meetingManager.audioVideo.realtimeSubscribeToVolumeIndicator(attendee, handler);
            }
            destructors.push(() => {
                if (meetingManager.audioVideo) {
                    meetingManager.audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendee, handler);
                }
            });
        });
        return () => {
            destructors.forEach(destructor => destructor());
        };
    }, [attendees]);
    return focusedAttendee;
};
const updateScore = (scores, attendeeId, score, force = false) => {
    if (Object.is(scores[attendeeId], undefined) || force) {
        scores[attendeeId] = score;
    }
    scores[attendeeId] = addValueToMovingAverage(scores[attendeeId], score);
    return scores;
};
const clearInvisibleScores = (scores, attendees) => {
    const filtered = {};
    Object.entries(scores).forEach(([attendee, score]) => {
        if (attendees.includes(attendee)) {
            filtered[attendee] = score;
        }
    });
    return filtered;
};
const degradeScores = (scores) => {
    Object.keys(scores).forEach(attendee => {
        scores[attendee] = addValueToMovingAverage(scores[attendee], 0);
    });
    return scores;
};
const addValueToMovingAverage = (currentValue, newValue) => (currentValue * MOVING_AVERAGE_LENGTH + newValue) / (MOVING_AVERAGE_LENGTH + 1);
//# sourceMappingURL=attendees.js.map